import { useCallback, useRef, useState } from "react"

/**
 * Promise-based replacement for window.confirm()/window.prompt().
 *
 * confirm("Delete this?") -> Promise<boolean>
 * confirm({ message, danger: true }) -> Promise<boolean>
 * confirm({ message, inputLabel: "Reason (optional)" }) -> Promise<string|null>
 *   (null = cancelled, string = submitted value, possibly empty)
 */
export function useConfirm() {
    const [confirmState, setConfirmState] = useState(null)
    const resolverRef = useRef(null)

    const confirm = useCallback((options) => {
        const state = typeof options === "string" ? { message: options } : { ...options }
        return new Promise((resolve) => {
            resolverRef.current = resolve
            setConfirmState(state)
        })
    }, [])

    const handleConfirm = useCallback((value) => {
        const state = confirmState
        resolverRef.current?.(state?.inputLabel ? (value ?? "") : true)
        resolverRef.current = null
        setConfirmState(null)
    }, [confirmState])

    const handleCancel = useCallback(() => {
        const state = confirmState
        resolverRef.current?.(state?.inputLabel ? null : false)
        resolverRef.current = null
        setConfirmState(null)
    }, [confirmState])

    return { confirmState, confirm, handleConfirm, handleCancel }
}
